import { Box, Button, HStack, Spinner, VStack, useToast } from '@chakra-ui/react';
import { usePropertiesModel } from '@common/models/PropertiesModel';
import { useWaitlistModel } from '@common/models/WaitlistModel';
import { FontFamily } from '@common/theming/Fonts';
import currency from '@common/utility/currency';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { TextBlock } from 'src/apps/invest/common/components/TextBlock';
import { usePropertiesDetailViewModel } from '../../models/PropertyDetailViewModel';

type WaitlistProps = {
  children?: React.ReactNode;
};

export const Waitlist: React.FC<WaitlistProps> = () => {
  const { property } = usePropertiesModel();
  const viewModel = usePropertiesDetailViewModel();
  const totalPropertyAmount = Number(property?.target_amount ?? 0);
  const currentAmountRaised = Number(property?.raised_amount?.total ?? 0);
  const fundAmountAvailable = totalPropertyAmount - currentAmountRaised;
  const fundedPercent = (currentAmountRaised / totalPropertyAmount) * 100;
  const waitlistModel = useWaitlistModel();
  const toast = useToast();
  const { id = '' } = useParams();
  const [viewState, setViewState] = React.useState<'JOIN' | 'IN_LINE' | 'PROCESSING' | 'UNKNOWN'>('JOIN');

  let totalInvestors = Number(property?.investor_count?.total);
  if (isNaN(totalInvestors)) totalInvestors = 0;

  useEffect(() => {
    if (waitlistModel.waitlistPosition) {
      setViewState('IN_LINE');
    } else {
      setViewState('JOIN');
    }
  }, []);

  async function handleWaitlistJoin() {
    setViewState('PROCESSING');
    await viewModel.joinWaitlist();

    // account for eventual consistency, give the BE a hot second to catch up
    setTimeout(async () => {
      await viewModel.init(id);
      setViewState('IN_LINE');
    }, 2000);
  }

  async function handleLinkShare() {
    const shareLink = window.location.host + '/waitlist/?referralCode=' + waitlistModel.waitlistPosition?.referral_code;
    await navigator.clipboard.writeText(shareLink);
    toast({
      description: 'Share the link with your friends to get a better waitlist position',
      duration: 3000,
      isClosable: true,
      position: 'top',
      status: 'info',
      title: 'Share link copied to your clipboard',
    });
  }

  return (
    <VStack gap={5} alignItems={'flex-start'} justifyItems={'flex-start'} w="full">
      <HStack alignItems={'flex-end'}>
        <TextBlock color="blue.500" fontSize={'40px'} weight="bold" lineHeight={1}>
          {currency.format(fundAmountAvailable ?? 0, { maximumFractionDigits: 0 })}
        </TextBlock>
        <TextBlock fontSize={'16px'} color="gray.600">
          AVAILABLE
        </TextBlock>
      </HStack>
      <Box backgroundColor="gray.100" borderRadius={15} h={15} w={'full'}>
        <Box
          background={'linear-gradient(270deg, #308EFF 0%, #034089 100%)'}
          borderRadius={15}
          h={15}
          minW={fundedPercent < 5 ? '5%' : '0%'}
          w={`${fundedPercent}%`}></Box>
      </Box>
      <HStack gap="32px">
        <HStack justifyItems={'flex-start'} alignItems={'flex-end'}>
          <TextBlock fontSize={'24px'} weight="bold" lineHeight={1}>
            {totalInvestors}
          </TextBlock>
          <TextBlock fontSize={'16px'} color="gray.600">
            {Number(totalInvestors) === 1 ? 'INVESTOR' : 'INVESTORS'}
          </TextBlock>
        </HStack>
        <HStack justifyItems={'flex-start'} alignItems={'flex-end'}>
          <TextBlock fontSize={'24px'} weight="bold" lineHeight={1}>
            {fundedPercent.toFixed(0)}%
          </TextBlock>
          <TextBlock fontSize={'16px'} color="gray.600">
            {'RAISED'}
          </TextBlock>
        </HStack>
      </HStack>

      {viewState === 'IN_LINE' && waitlistModel.waitlistPosition && (
        <Box padding="30px" flexDirection={'column'} borderColor="blue.100" borderWidth="2px" borderRadius={'10px'}>
          <TextBlock color="blue.500" weight="bold" fontSize="21px" textAlign={'left'}>
            You're in line!
          </TextBlock>
          <TextBlock>
            You've joined our waitlist and therefore part of our priority access list. Your current position is{' '}
            <TextBlock as="span" weight="bold">
              {waitlistModel.waitlistPosition.position}
            </TextBlock>{' '}
            <TextBlock as="span">but you can move up if you refer a friend, just share</TextBlock>{' '}
            <Button as="span" colorScheme='"yellow' variant="link" onClick={handleLinkShare}>
              this link
            </Button>
            . Stay tuned for announcements.
          </TextBlock>
        </Box>
      )}

      {viewState === 'JOIN' && (
        <Box
          _hover={{
            background: `linear-gradient(270deg, #308EFF 0%, #308EFF 100%);`,
            cursor: 'pointer',
          }}
          background={`linear-gradient(270deg, #308EFF 0%, #034089 100%);`}
          borderRadius={'5px'}
          backgroundColor={'blue.500'}
          color={'white'}
          fontFamily={FontFamily.HELVETICA_NOW_BOLD}
          fontSize={'18px'}
          lineHeight={'60px'}
          onClick={handleWaitlistJoin}
          textAlign={'center'}
          w={'full'}>
          Join Waitlist
        </Box>
      )}

      {viewState === 'PROCESSING' && (
        <Box
          display={'flex'}
          background={`linear-gradient(270deg, #308EFF 0%, #034089 100%);`}
          borderRadius={'5px'}
          backgroundColor={'blue.500'}
          color={'white'}
          height={'60px'}
          alignItems={'center'}
          justifyContent={'center'}
          w={'full'}>
          <Spinner />
        </Box>
      )}
    </VStack>
  );
};
